// RÉSZTVEVŐ-ÜTKÖZÉS mentés ELŐTT — a kliens-oldali fél (a szabály a BFF-ben: `meetings-participant-conflicts.ts`).
//
// FIGYELMEZTETÉS, NEM TILTÁS (user-döntés): a felület sárga, megerősítéssel átléphető jelzést ad. Ezért a lekérés
// BEST-EFFORT: hiba (hálózat, 403, 5xx) esetén NINCS figyelmeztetés és a mentés megy tovább — egy elérhetetlen
// ellenőrzés nem akaszthatja meg a megbeszélés rögzítését.
import { type MeetingParticipantConflictSchema } from '@atlas/contracts';
import { type RecurrenceRule } from '@atlas/shared';
import { type z } from 'zod';

import { api } from '@/lib/api';

export type MeetingParticipantConflict = z.infer<typeof MeetingParticipantConflictSchema>;

/** A szerkesztés előtti állapot (create-nél `null`). */
export interface ConflictCheckOriginal {
  startsAt: string | null;
  endsAt: string | null;
  employeeIds: readonly string[];
  /** Akik ERRE a megbeszélésre már lemondták a részvételt — nem jönnek, tehát nem is ütközhetnek. */
  declinedIds?: readonly string[];
}

/** Ugyanaz az időpont-e (hiányzó/érvénytelen érték → NEM ugyanaz, azaz „változott"). */
function sameInstant(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  const ta = Date.parse(a);
  return !Number.isNaN(ta) && ta === Date.parse(b);
}

/**
 * KIKET kell ellenőrizni. Létrehozásnál mindenkit. Szerkesztésnél: ha az IDŐPONT változott, mindenkit (az új
 * idősáv bárkit ütköztethet); ha csak a résztvevők, CSAK az újonnan felvetteket — a már bent lévők ütközését a
 * felhasználó egyszer már látta és megerősítette, egy címjavításnál ne ugorjon fel újra. Aki erre a megbeszélésre
 * már lemondta a részvételt, az egyik esetben sem számít.
 */
export function employeesToCheck(
  next: ConflictCheckOriginal,
  original: ConflictCheckOriginal | null,
): string[] {
  if (original === null) return [...next.employeeIds];
  const declined = new Set(original.declinedIds ?? []);
  const attending = next.employeeIds.filter((id) => !declined.has(id));
  // instant-összevetés, nem string: a szerver ISO-alakja (ezredmásodperc) eltérhet a formból visszaalakítottól
  const timeChanged =
    !sameInstant(next.startsAt, original.startsAt) || !sameInstant(next.endsAt, original.endsAt);
  if (timeChanged) return attending;
  const before = new Set(original.employeeIds);
  return attending.filter((id) => !before.has(id));
}

/**
 * A best-effort ellenőrzés felső időkorlátja: egy lassú Odin ne tartsa fel a mentést / az átütemezést — utána
 * figyelmeztetés nélkül megyünk tovább (ugyanaz, mint a hiba-ág).
 */
export const CONFLICT_CHECK_TIMEOUT_MS = 4000;

/** A kért munkatársak ütközései (best-effort: hiba vagy időtúllépés → üres lista, azaz nincs figyelmeztetés). */
export async function fetchParticipantConflicts(params: {
  startsAt: string;
  endsAt: string;
  employeeIds: readonly string[];
  excludeId?: string | undefined;
  /** Új SOROZAT szabálya — a BFF minden alkalomra ellenőriz (ugyanazzal a kibontással, mint a mentés). */
  recurrence?: RecurrenceRule | null | undefined;
}): Promise<MeetingParticipantConflict[]> {
  if (params.employeeIds.length === 0) return [];
  try {
    const request = api.meetingParticipantConflicts.query({
      query: {
        startsAt: params.startsAt,
        endsAt: params.endsAt,
        employeeIds: [...params.employeeIds],
        ...(params.excludeId ? { excludeId: params.excludeId } : {}),
        ...(params.recurrence
          ? {
              frequency: params.recurrence.frequency,
              weekdays: [...params.recurrence.weekdays],
              ...(params.recurrence.untilDate ? { untilDate: params.recurrence.untilDate } : {}),
              ...(params.recurrence.occurrenceCount
                ? { occurrenceCount: params.recurrence.occurrenceCount }
                : {}),
            }
          : {}),
      },
    });
    const timeout = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), CONFLICT_CHECK_TIMEOUT_MS);
    });
    const res = await Promise.race([request, timeout]);
    return res?.status === 200 ? res.body.conflicts : [];
  } catch {
    // a hálózati hiba NEM blokkolja a mentést — a figyelmeztetés elmarad, a szerver-oldali falak (helyszín,
    // Zoom) ettől függetlenül a mentésen futnak
    return [];
  }
}
